import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm } from "@inertiajs/react"
import { IconArrowLeft, IconInfoCircle } from "@tabler/icons-react"

export default function InvoiceUpload() {
  const { data, setData, post, processing } = useForm<{ pdf_files: File[] }>({
    pdf_files: [],
  })

  const readLabel = data.pdf_files.length > 1 ? `Read ${data.pdf_files.length} PDFs` : "Read PDF"

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/invoices/extract", { forceFormData: true })
  }

  return (
    <>
      <Head title="Upload Invoice PDFs - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Upload PDF</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/invoices">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Upload Invoice PDFs</h1>
              <p className="text-muted-foreground text-sm">Read the details off each PDF, then review before saving</p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Select PDFs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="pdf_files">Invoice PDFs</Label>
                  <input
                    id="pdf_files"
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    required
                    onChange={(e) => setData("pdf_files", Array.from(e.target.files || []))}
                    className="border-input bg-background block w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                  />
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                  <div className="flex gap-3">
                    <IconInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-300">What happens next</p>
                      <ul className="mt-2 list-inside list-disc space-y-1 text-blue-700 dark:text-blue-400">
                        <li>Customer, currency, dates and total are read off each PDF</li>
                        <li>Invoices already recorded are flagged so you do not add them twice</li>
                        <li>You review and correct everything before anything is saved</li>
                        <li>Nothing is written to the database or the Revenue sheet until you confirm</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/invoices">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || data.pdf_files.length === 0}>
                  {processing ? "Reading..." : readLabel}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

InvoiceUpload.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
